import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCopy, faSort, faPause, faPlay, faClock, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import initParticles from '../constants/particles.js';
import './TokenManagement.css';

const TokenManagement = () => {
  const [tokens, setTokens] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [newToken, setNewToken] = useState({ durationDays: '', description: '' });
  const [durationType, setDurationType] = useState('custom');
  const [actionLoading, setActionLoading] = useState({});
  const [message, setMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [tokenToAction, setTokenToAction] = useState(null);
  const [formErrors, setFormErrors] = useState({ durationDays: '' });
  const [copiedToken, setCopiedToken] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [showUUIDs, setShowUUIDs] = useState({});
  const [showAdjustTimeModal, setShowAdjustTimeModal] = useState(false);
  const [adjustTimeDays, setAdjustTimeDays] = useState('');
  const [adjustTimeError, setAdjustTimeError] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [selectedTokenKeys, setSelectedTokenKeys] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');
  const [tempVersion, setTempVersion] = useState('');
  const [versionError, setVersionError] = useState('');
  const [updatingVersion, setUpdatingVersion] = useState(false);
  const canvasRef = useRef(null);

  localStorage.setItem("redirectAfterLogin", window.location.pathname);
  const navigate = useNavigate();

  useEffect(() => {
      fetchTokens();
      const interval = setInterval(fetchTokens, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
  }, [navigate]);

  // Token Management Functions
  const verifyToken = (token) => {
    const decoded = decodeToken(token);
    if (!decoded) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp > currentTime; // Valid if token expiration is in the future
  };

  const decodeToken = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (err) {
      console.error("Invalid token format:", err);
      return null;
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        console.error("No refresh token found in local storage.");
        throw new Error("No refresh token available.");
      }

      const response = await axios.post(
        "/api/auth/refresh-token",
        {},
        {
          headers: {
            "x-refresh-token": refreshToken,
          },
        }
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Update local storage with new tokens
      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", newRefreshToken);

      console.log(`[${new Date().toISOString()}] Access token refreshed successfully`);
      return accessToken;
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Token refresh failed:`, err);
      handleLogout();
      return null;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setIsAuthorized(false);
    navigate('/');
  };

  // API Request Wrapper with Token Refresh
  const makeApiRequest = async (method, url, data = null, config = {}) => {
    let token = localStorage.getItem('token');
    if (!token) {
      console.log(`[${new Date().toISOString()}] No token found, redirecting to login`);
      handleLogout();
      return null;
    }

    // Check if token is expired
    if (!verifyToken(token)) {
      console.log(`[${new Date().toISOString()}] Access token expired, attempting to refresh`);
      token = await refreshAccessToken();
      if (!token) {
        return null;
      }
    }

    try {
      const response = await axios({
        method,
        url,
        data,
        headers: {
          Authorization: `Bearer ${token}`,
          ...config.headers,
        },
        ...config,
      });
      return response;
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        console.log(`[${new Date().toISOString()}] Unauthorized request, attempting token refresh`);
        token = await refreshAccessToken();
        if (!token) {
          return null;
        }
        // Retry the request with the new token
        try {
          const retryResponse = await axios({
            method,
            url,
            data,
            headers: {
              Authorization: `Bearer ${token}`,
              ...config.headers,
            },
            ...config,
          });
          return retryResponse;
        } catch (retryError) {
          console.error(`[${new Date().toISOString()}] Retry failed after token refresh:`, retryError);
          handleLogout();
          return null;
        }
      } else {
        throw error;
      }
    }
  };

  // Periodic Token Refresh
  useEffect(() => {
    const interval = setInterval(async () => {
      const token = localStorage.getItem('token');
      if (token && !verifyToken(token)) {
        console.log(`[${new Date().toISOString()}] Periodic token check: token expired, refreshing`);
        await refreshAccessToken();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    console.log('Debug [Dev/Prod]: Mounting TokenManagement component');
    
    const initializeParticles = () => {
      if (canvasRef.current) {
        console.log('Debug [Dev/Prod]: Canvas ref available, initializing particles');
        try {
          const cleanup = initParticles(canvasRef.current);
          return cleanup;
        } catch (error) {
          console.error('Debug [Dev/Prod]: Particle initialization failed:', error);
        }
      } else {
        console.warn('Debug [Dev/Prod]: Canvas ref is null');
      }
    };

    const cleanup = initializeParticles();

    const checkInterval = setInterval(() => {
      if (canvasRef.current && !cleanup) {
        console.log('Debug [Dev/Prod]: Retrying particle initialization');
        const retryCleanup = initializeParticles();
        if (retryCleanup) {
          clearInterval(checkInterval);
        }
      }
    }, 1000);

    return () => {
      console.log('Debug [Dev/Prod]: Unmounting component');
      if (cleanup) cleanup();
      clearInterval(checkInterval);
    };
  }, []);

  const validateCreateForm = () => {
    let errors = { durationDays: '' };
    if (durationType === 'custom') {
      if (!newToken.durationDays) {
        errors.durationDays = 'Duration is required';
      } else if (isNaN(newToken.durationDays) || parseInt(newToken.durationDays) < 1) {
        errors.durationDays = 'Duration must be at least 1 day';
      }
    }
    setFormErrors(errors);
    return !errors.durationDays;
  };

  const validateVersionForm = () => {
    if (!tempVersion || tempVersion.trim() === '') {
      setVersionError('Version is required');
      return false;
    }
    if (!/^\d+\.\d+\.\d+$/.test(tempVersion)) {
      setVersionError('Version must be in format x.y.z (e.g., 1.0.0)');
      return false;
    }
    setVersionError('');
    return true;
  };

  const fetchTokens = async () => {
    setLoadingTokens(true);
    try {
      const response = await makeApiRequest('get', '/api/admin/tokens');
      if (!response) {
        setIsAuthorized(false);
        return;
      }
      setTokens(response.data.tokens || []);
      if (response.data.tokens && response.data.tokens.length > 0) {
        setCurrentVersion(response.data.tokens[0].version || '1.0.0');
        setTempVersion(response.data.tokens[0].version || '1.0.0');
      } else {
        setCurrentVersion('1.0.0');
        setTempVersion('1.0.0');
      }
      setIsAuthorized(true);
    } catch (error) {
      console.log(`[${new Date().toISOString()}] Error fetching tokens: ${error.response?.data?.message || error.message}`);
      setIsAuthorized(false);
    } finally {
      setLoadingTokens(false);
    }
  };

  const createToken = async (e) => {
    e.preventDefault();
    if (!validateCreateForm()) {
      setMessage({
        text: 'Please fix form errors before submitting.',
        type: 'error',
      });
      return;
    }

    let durationInDays;
    switch (durationType) {
      case '1week':
        durationInDays = 7;
        break;
      case '1month':
        durationInDays = 30;
        break;
      case '1year':
        durationInDays = 365;
        break;
      case 'lifetime':
        durationInDays = 'lifetime';
        break;
      default:
        durationInDays = parseInt(newToken.durationDays);
    }

    try {
      setActionLoading((prev) => ({ ...prev, create: true }));
      const response = await makeApiRequest('post', '/api/admin/tokens/create', {
        durationDays: durationInDays,
        description: newToken.description || 'General access token',
      });
      if (!response) {
        return;
      }
      setMessage({
        text: 'Token created successfully.',
        type: 'success',
      });
      setNewToken({ durationDays: '', description: '' });
      setDurationType('custom');
      setFormErrors({ durationDays: '' });
      await fetchTokens();
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized token creation: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error creating token.',
          type: 'error',
        });
        console.error('Error creating token:', error);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const updateVersion = async (e) => {
    e.preventDefault();
    if (!validateVersionForm()) {
      setMessage({
        text: 'Please fix version errors before submitting.',
        type: 'error',
      });
      return;
    }

    try {
      setUpdatingVersion(true);
      const response = await makeApiRequest('put', '/api/admin/tokens/update-versions', {
        newVersion: tempVersion,
      });
      if (!response) {
        return;
      }
      setMessage({
        text: `Version updated to ${tempVersion} for ${response.data.modifiedCount} tokens.`,
        type: 'success',
      });
      setCurrentVersion(tempVersion);
      await fetchTokens();
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized version update: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error updating version.',
          type: 'error',
        });
        console.error('Error updating version:', error);
      }
    } finally {
      setUpdatingVersion(false);
    }
  };

  const revokeToken = async () => {
    if (!tokenToAction) return;
    try {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: true }));
      const response = await makeApiRequest('post', '/api/admin/tokens/revoke', { key: tokenToAction });
      if (!response) {
        return;
      }
      setMessage({
        text: 'Token revoked successfully.',
        type: 'success',
      });
      await fetchTokens();
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized token revocation: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error revoking token.',
          type: 'error',
        });
        console.error('Error revoking token:', error);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: false }));
      closeConfirmModal();
    }
  };

  const deleteToken = async () => {
    if (!tokenToAction) return;
    try {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: true }));
      const response = await makeApiRequest('post', '/api/admin/tokens/delete', { key: tokenToAction });
      if (!response) {
        return;
      }
      setMessage({
        text: 'Token deleted successfully.',
        type: 'success',
      });
      await fetchTokens();
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized token deletion: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error deleting token.',
          type: 'error',
        });
        console.error('Error deleting token:', error);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: false }));
      closeConfirmModal();
    }
  };

  const pauseToken = async () => {
    if (!tokenToAction) return;
    try {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: true }));
      const response = await makeApiRequest('post', '/api/admin/tokens/pause', { key: tokenToAction });
      if (!response) {
        return;
      }
      setMessage({
        text: 'Token paused successfully.',
        type: 'success',
      });
      await fetchTokens();
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized token pause: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error pausing token.',
          type: 'error',
        });
        console.error('Error pausing token:', error);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: false }));
      closeConfirmModal();
    }
  };

  const resumeToken = async () => {
    if (!tokenToAction) return;
    try {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: true }));
      const response = await makeApiRequest('post', '/api/admin/tokens/resume', { key: tokenToAction });
      if (!response) {
        return;
      }
      setMessage({
        text: 'Token resumed successfully.',
        type: 'success',
      });
      await fetchTokens();
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized token resume: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error resuming token.',
          type: 'error',
        });
        console.error('Error resuming token:', error);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: false }));
      closeConfirmModal();
    }
  };

  const adjustTokenTime = async () => {
    if (!tokenToAction || !adjustTimeDays) return;
    if (isNaN(adjustTimeDays) || parseInt(adjustTimeDays) === 0) {
      setAdjustTimeError('Please enter a valid number of days.');
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: true }));
      const response = await makeApiRequest('post', '/api/admin/tokens/adjust-time', {
        key: tokenToAction,
        days: parseInt(adjustTimeDays),
      });
      if (!response) {
        return;
      }
      setMessage({
        text: `Token duration adjusted by ${adjustTimeDays} days.`,
        type: 'success',
      });
      setAdjustTimeDays('');
      setAdjustTimeError('');
      await fetchTokens();
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized time adjustment: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error adjusting token time.',
          type: 'error',
        });
        console.error('Error adjusting token time:', error);
      }
    } finally {
      setActionLoading((prev) => ({ ...prev, [tokenToAction]: false }));
      closeAdjustTimeModal();
    }
  };

  const banUUID = async (key, uuid) => {
      const reason = prompt('Please enter a reason for banning this UUID:');
      if (!reason) {
          setMessage({ text: 'Ban reason is required.', type: 'error' });
          return;
      }

      try {
          setActionLoading((prev) => ({ ...prev, [`${key}-${uuid}`]: true }));
          const response = await makeApiRequest('post', '/api/admin/tokens/ban-uuid', {
              key,
              uuid, // Pass the UUID string
              reason,
          });
          if (!response) {
              return;
          }
          setMessage({ text: `UUID ${uuid} banned successfully.`, type: 'success' });
          await fetchTokens();
      } catch (error) {
          const status = error.response?.status;
          if (status === 400 || status === 401 || status === 403 || status === 404) {
              console.log(`[${new Date().toISOString()}] Unauthorized UUID ban: ${error.response?.data?.message}`);
              navigate('/');
          } else {
              setMessage({
                  text: error.response?.data?.message || 'Error banning UUID.',
                  type: 'error',
              });
              console.error('Error banning UUID:', error);
          }
      } finally {
          setActionLoading((prev) => ({ ...prev, [`${key}-${uuid}`]: false }));
      }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageContent.trim()) {
      setMessage({ text: 'Message content is required.', type: 'error' });
      return;
    }

    try {
      setSendingMessage(true);
      const payload = { content: messageContent };
      if (selectedTokenKeys.length > 0) {
        payload.tokenKeys = selectedTokenKeys;
      }
      const response = await makeApiRequest('post', '/api/admin/tokens/send-message', payload);
      if (!response) {
        return;
      }
      setMessage({
        text: `Message sent to ${selectedTokenKeys.length > 0 ? selectedTokenKeys.length : 'all active/paused'} token(s).`,
        type: 'success',
      });
      setMessageContent('');
      setSelectedTokenKeys([]);
    } catch (error) {
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403 || status === 404) {
        console.log(`[${new Date().toISOString()}] Unauthorized message send: ${error.response?.data?.message}`);
        navigate('/');
      } else {
        setMessage({
          text: error.response?.data?.message || 'Error sending message.',
          type: 'error',
        });
        console.error('Error sending message:', error);
      }
    } finally {
      setSendingMessage(false);
    }
  };

  const toggleUUIDsVisibility = (tokenKey) => {
    setShowUUIDs((prev) => ({
      ...prev,
      [tokenKey]: !prev[tokenKey],
    }));
  };

  const openConfirmModal = (key, action) => {
    setTokenToAction(key);
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setTokenToAction(null);
    setConfirmAction(null);
  };

  const openAdjustTimeModal = (key) => {
    setTokenToAction(key);
    setShowAdjustTimeModal(true);
  };

  const closeAdjustTimeModal = () => {
    setShowAdjustTimeModal(false);
    setTokenToAction(null);
    setAdjustTimeDays('');
    setAdjustTimeError('');
  };

  const copyToken = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedToken(key);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      setMessage({
        text: 'Failed to copy token.',
        type: 'error',
      });
      console.error('Error copying token:', error);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log(`[${new Date().toISOString()}] No token found, redirecting to home`);
      setIsAuthorized(false);
      return;
    }
    fetchTokens();
  }, [navigate]);

  useEffect(() => {
    if (message) {
      const timeout = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timeout);
    }
  }, [message]);

  if (isAuthorized === false) {
    return <Navigate to="/" />;
  }

  if (isAuthorized === null) {
    return null;
  }

  const filteredTokens = tokens.filter((token) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (token.key?.toLowerCase() || '').includes(searchLower) ||
      (token.description?.toLowerCase() || '').includes(searchLower) ||
      (token.status?.toLowerCase() || '').includes(searchLower)
    );
  });

  const sortedTokens = [...filteredTokens].sort((a, b) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    if (sortConfig.key === 'createdAt' || sortConfig.key === 'expiresAt') {
      return direction * (new Date(a[sortConfig.key]) - new Date(b[sortConfig.key]));
    }
    return direction * (a[sortConfig.key]?.localeCompare(b[sortConfig.key]) || 0);
  });

  return (
    <div className="token-management-container">
      <div className="background-animation-wrapper">
        <canvas ref={canvasRef} className="particle-canvas"></canvas>
      </div>
      {message && (
        <div className={`token-management-alert token-management-alert-${message.type}`}>
          <span>{message.text}</span>
        </div>
      )}

      {/* Version Management Section (Expandable) */}
      <div className="token-management-form-section">
        <details className="token-management-expandable">
          <summary className="token-management-expandable-summary">
            Manage Version
          </summary>
          <div className="token-management-card glassmorphic-card">
            <form onSubmit={updateVersion} className="token-management-form">
              <div className="form-group">
                <label>Current Version</label>
                <input
                  type="text"
                  value={currentVersion}
                  className="token-management-input"
                  disabled
                />
              </div>
              <div className="form-group">
                <label htmlFor="tempVersion">New Version</label>
                <input
                  type="text"
                  id="tempVersion"
                  placeholder="Enter version (e.g., 1.0.0)"
                  value={tempVersion}
                  onChange={(e) => {
                    setTempVersion(e.target.value);
                    validateVersionForm();
                  }}
                  className={`token-management-input ${versionError ? 'input-error' : ''}`}
                  required
                  aria-describedby="versionError"
                />
                {versionError && (
                  <span id="versionError" className="error-message">
                    {versionError}
                  </span>
                )}
              </div>
              <button
                type="submit"
                className="token-management-button token-management-button-primary subtle-glow"
                disabled={updatingVersion}
                data-tooltip="Update version for all tokens"
              >
                {updatingVersion ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Update Version'}
              </button>
            </form>
          </div>
        </details>
      </div>

      {/* Create Token Form (Non-Expandable) */}
      <div className="token-management-form-section">
        <div className="token-management-card glassmorphic-card">
          <h3 className="token-management-subtitle">Create New Token</h3>
          <form onSubmit={createToken} className="token-management-form">
            <div className="form-group">
              <label htmlFor="durationType">Duration</label>
              <select
                id="durationType"
                value={durationType}
                onChange={(e) => {
                  setDurationType(e.target.value);
                  if (e.target.value !== 'custom') {
                    setNewToken({ ...newToken, durationDays: '' });
                    setFormErrors({ ...formErrors, durationDays: '' });
                  }
                }}
                className="token-management-input duration-select"
              >
                <option value="custom">Days</option>
                <option value="1week">1 Week</option>
                <option value="1month">1 Month</option>
                <option value="1year">1 Year</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </div>
            {durationType === 'custom' && (
              <div className="form-group">
                <label htmlFor="durationDays">Custom Duration (days)</label>
                <input
                  type="number"
                  id="durationDays"
                  placeholder="Enter duration in days"
                  value={newToken.durationDays}
                  onChange={(e) => {
                    setNewToken({ ...newToken, durationDays: e.target.value });
                    validateCreateForm();
                  }}
                  className={`token-management-input ${formErrors.durationDays ? 'input-error' : ''}`}
                  min="1"
                  required={durationType === 'custom'}
                  aria-describedby="durationDaysError"
                />
                {formErrors.durationDays && (
                  <span id="durationDaysError" className="error-message">
                    {formErrors.durationDays}
                  </span>
                )}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="description">Description (optional)</label>
              <input
                type="text"
                id="description"
                placeholder="Enter token description"
                value={newToken.description}
                onChange={(e) => setNewToken({ ...newToken, description: e.target.value })}
                className="token-management-input"
              />
            </div>
            <button
              type="submit"
              className="token-management-button token-management-button-primary subtle-glow"
              disabled={actionLoading.create}
              data-tooltip="Create a new access token"
            >
              {actionLoading.create ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Create Token'}
            </button>
          </form>
        </div>
      </div>

      {/* Send Message Form */}
      <div className="token-management-form-section">
        <div className="token-management-card glassmorphic-card">
          <h3 className="token-management-subtitle">Send Message to Tokens</h3>
          <form onSubmit={sendMessage} className="token-management-form">
            <div className="form-group">
              <label htmlFor="messageContent">Message Content</label>
              <textarea
                id="messageContent"
                placeholder="Enter message to send"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                className="token-management-input"
                rows="4"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="tokenKeys">Select Tokens (leave blank for all active/paused tokens)</label>
              <select
                id="tokenKeys"
                multiple
                value={selectedTokenKeys}
                onChange={(e) => setSelectedTokenKeys(Array.from(e.target.selectedOptions, option => option.value))}
                className="token-management-input duration-select"
              >
                {tokens
                  .filter(token => ['active', 'paused'].includes(token.status))
                  .map(token => (
                    <option key={token.key} value={token.key}>
                      {token.key.substring(0, 8)}... ({token.description})
                    </option>
                  ))}
              </select>
            </div>
            <button
              type="submit"
              className="token-management-button token-management-button-primary subtle-glow"
              disabled={sendingMessage}
              data-tooltip="Send message to selected or all active/paused tokens"
            >
              {sendingMessage ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faPaperPlane} /> Send Message</>}
            </button>
          </form>
        </div>
      </div>

      {/* Token Table */}
      <div className="token-management-table-section">
        {loadingTokens ? (
          <div className="loading-spinner futuristic-spinner">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        ) : sortedTokens.length === 0 ? (
          <p className="token-management-text">No tokens available.</p>
        ) : (
          <div className="token-management-table-container">
            <h3 className="token-management-subtitle">Existing Tokens</h3>
            <div className="table-controls">
              <input
                type="text"
                placeholder="Search tokens..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="token-search-input"
              />
            </div>
            <table className="token-management-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('key')}>
                    Token Key
                    {sortConfig.key === 'key' && (
                      <span className="sort-icon">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('description')}>
                    Description
                    {sortConfig.key === 'description' && (
                      <span className="sort-icon">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('status')}>
                    Status
                    {sortConfig.key === 'status' && (
                      <span className="sort-icon">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th>Redeemed</th>
                  <th onClick={() => handleSort('createdAt')}>
                    Created At
                    {sortConfig.key === 'createdAt' && (
                      <span className="sort-icon">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th onClick={() => handleSort('expiresAt')}>
                    Expires At
                    {sortConfig.key === 'expiresAt' && (
                      <span className="sort-icon">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTokens.map((token) => {
                  // Determine if any UUID is online
                  const isAnyUuidOnline = token.usedUUIDs?.some(
                    (entry) => entry.lastPing && (new Date() - new Date(entry.lastPing)) < 30 * 1000
                  );
                  return (
                    <React.Fragment key={token.key}>
                      <tr className="token-row">
                        <td>
                          <div className="token-key-wrapper">
                            <span className="token-key">{token.key.substring(0, 16)}...</span>
                            <button
                              onClick={() => copyToken(token.key)}
                              className="token-management-button token-management-button-copy neon-glow-btn"
                              data-tooltip="Copy full token"
                              aria-label="Copy token"
                            >
                              <FontAwesomeIcon icon={faCopy} />
                              {copiedToken === token.key && <span className="copied-feedback">Copied!</span>}
                            </button>
                          </div>
                        </td>
                        <td>{token.description}</td>
                        <td className={`status-${token.status}`}>
                          {token.status.charAt(0).toUpperCase() + token.status.slice(1)}
                        </td>
                        <td>
                          {token.usedUUIDs && token.usedUUIDs.length > 0 ? (
                            <>
                              Yes ({token.usedUUIDs.length})
                              <span
                                className={isAnyUuidOnline ? "status-online" : "status-offline"}
                                style={{ marginLeft: '10px' }}
                              >
                                {isAnyUuidOnline ? 'Online' : 'Offline'}
                              </span>
                              <button
                                onClick={() => toggleUUIDsVisibility(token.key)}
                                className="token-management-button token-management-button-secondary"
                                data-tooltip={showUUIDs[token.key] ? 'Hide UUIDs' : 'Show UUIDs'}
                              >
                                {showUUIDs[token.key] ? 'Hide UUIDs' : 'Show UUIDs'}
                              </button>
                            </>
                          ) : (
                            'No'
                          )}
                        </td>
                        <td>{new Date(token.createdAt).toLocaleString()}</td>
                        <td>
                          {token.durationDays === 'lifetime'
                            ? 'Lifetime'
                            : token.isActivated && token.expiresAt
                            ? new Date(token.expiresAt).toLocaleString()
                            : 'Not Activated'}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <div className="action-row">
                              {token.status === 'paused' ? (
                                <button
                                  onClick={() => openConfirmModal(token.key, 'resume')}
                                  className="token-management-button token-management-button-primary neon-glow-btn"
                                  disabled={actionLoading[token.key]}
                                  data-tooltip="Resume this token"
                                  aria-label="Resume token"
                                >
                                  {actionLoading[token.key] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlay} />}
                                </button>
                              ) : (
                                <button
                                  onClick={() => openConfirmModal(token.key, 'pause')}
                                  className="token-management-button token-management-button-primary neon-glow-btn"
                                  disabled={actionLoading[token.key] || token.status !== 'active'}
                                  data-tooltip="Pause this token"
                                  aria-label="Pause token"
                                >
                                  {actionLoading[token.key] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPause} />}
                                </button>
                              )}
                              <button
                                onClick={() => openAdjustTimeModal(token.key)}
                                className="token-management-button token-management-button-primary neon-glow-btn"
                                disabled={actionLoading[token.key] || token.status !== 'active' || token.durationDays === 'lifetime'}
                                data-tooltip="Adjust token duration"
                                aria-label="Adjust token duration"
                              >
                                {actionLoading[token.key] ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faClock} />}
                              </button>
                            </div>
                            <div className="action-row">
                              <button
                                onClick={() => openConfirmModal(token.key, 'revoke')}
                                className="token-management-button token-management-button-error neon-glow-btn"
                                disabled={actionLoading[token.key] || token.status !== 'active'}
                                data-tooltip="Revoke this token"
                                aria-label="Revoke token"
                              >
                                {actionLoading[token.key] ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Revoke'}
                              </button>
                              <button
                                onClick={() => openConfirmModal(token.key, 'delete')}
                                className="token-management-button token-management-button-error neon-glow-btn"
                                disabled={actionLoading[token.key]}
                                data-tooltip="Delete this token"
                                aria-label="Delete token"
                              >
                                {actionLoading[token.key] ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {showUUIDs[token.key] && token.usedUUIDs && token.usedUUIDs.length > 0 && (
                        <tr>
                          <td colSpan="7">
                            <div className="uuid-list">
                              <h5>Redeemed UUIDs:</h5>
                              <ul>
                                {token.usedUUIDs.map((entry, index) => {
                                  const { uuid, lastPing } = entry;
                                  const isOnline = lastPing && (new Date() - new Date(lastPing)) < 30 * 1000;
                                  const isBanned = token.bannedUUIDs?.some((banned) => banned.uuid === uuid);
                                  const banDetails = token.bannedUUIDs?.find((banned) => banned.uuid === uuid);
                                  console.log(`Rendering UUID: ${uuid}, isOnline: ${isOnline}, lastPing: ${lastPing}, isBanned: ${isBanned}`);
                                  return (
                                    <li key={`${uuid}-${index}`}>
                                      {uuid}
                                      {isOnline ? (
                                        <span className="status-online">Online</span>
                                      ) : (
                                        <span className="status-offline">Offline</span>
                                      )}
                                      {isBanned ? (
                                        <span className="banned-uuid">
                                          (Banned - Reason: {banDetails.reason}, At: {new Date(banDetails.bannedAt).toLocaleString()})
                                        </span>
                                      ) : (
                                        <button
                                          onClick={() => banUUID(token.key, uuid)}
                                          className="token-management-button token-management-button-ban neon-glow-btn"
                                          disabled={actionLoading[`${token.key}-${uuid}`]}
                                          data-tooltip="Blacklist this UUID"
                                        >
                                          {actionLoading[`${token.key}-${uuid}`] ? (
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                          ) : (
                                            'Blacklist'
                                          )}
                                        </button>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="confirmation-modal">
          <div className="modal-content glassmorphic-modal">
            <h3>
              {confirmAction === 'revoke'
                ? 'Confirm Revoke'
                : confirmAction === 'delete'
                ? 'Confirm Delete'
                : confirmAction === 'pause'
                ? 'Confirm Pause'
                : 'Confirm Resume'}
            </h3>
            <p>
              Are you sure you want to {confirmAction} this token? This action cannot be undone.
            </p>
            <div className="modal-button-container">
              <button
                onClick={
                  confirmAction === 'revoke'
                    ? revokeToken
                    : confirmAction === 'delete'
                    ? deleteToken
                    : confirmAction === 'pause'
                    ? pauseToken
                    : resumeToken
                }
                className="confirm-button neon-glow-btn"
                disabled={actionLoading[tokenToAction]}
              >
                {actionLoading[tokenToAction] ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  confirmAction === 'revoke'
                    ? 'Yes, Revoke'
                    : confirmAction === 'delete'
                    ? 'Yes, Delete'
                    : confirmAction === 'pause'
                    ? 'Yes, Pause'
                    : 'Yes, Resume'
                )}
              </button>
              <button onClick={closeConfirmModal} className="cancel-button neon-glow-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Time Modal */}
      {showAdjustTimeModal && (
        <div className="confirmation-modal">
          <div className="modal-content glassmorphic-modal">
            <h3>Adjust Token Duration</h3>
            <p>Enter the number of days to add (positive) or remove (negative) from the token's duration.</p>
            <div className="form-group">
              <label htmlFor="adjustTimeDays">Days</label>
              <input
                type="number"
                id="adjustTimeDays"
                placeholder="e.g., 7 or -7"
                value={adjustTimeDays}
                onChange={(e) => {
                  setAdjustTimeDays(e.target.value);
                  setAdjustTimeError('');
                }}
                className={`token-management-input ${adjustTimeError ? 'input-error' : ''}`}
              />
              {adjustTimeError && (
                <span className="error-message">{adjustTimeError}</span>
              )}
            </div>
            <div className="modal-button-container">
              <button
                onClick={adjustTokenTime}
                className="confirm-button neon-glow-btn"
                disabled={actionLoading[tokenToAction] || !adjustTimeDays}
              >
                {actionLoading[tokenToAction] ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Adjust Time'}
              </button>
              <button onClick={closeAdjustTimeModal} className="cancel-button neon-glow-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenManagement;